import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { BreadService } from '../bread.service';
import { Quote } from '@app/core/models/quote.models';
import { BaseCrudService } from '../bread-crud.service';
import { ApiService } from '../api.service';
import { LoaderService } from '../loader.service';

@Injectable({ providedIn: 'root' })
export class QuoteService extends BaseCrudService<Quote> {
  protected model = 'invoices';
  private api = inject(ApiService);
  private loader = inject(LoaderService);

  constructor(bread: BreadService) {
    super(bread);
  }

  /**
   * Send quote by email/WhatsApp
   */
  send(id: string | number, payload: { to?: string; subject?: string; message?: string; link?: string }): Observable<any> {
    this.loader.startLoading();
    return this.api.post<{ data: any }>(`kreosi/invoices/send-email/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error sending quote:', err);
          return throwError(() => err);
        }),
        finalize(() => this.loader.stopLoading())
      );
  }

  /**
   * Convert quote to invoice
   * @param id        Quote ID
   * @param payload   { invoice_kind: 'full'|'deposit'|'balance', deposit_rate?: number, deposit_amount?: number }
   */
  convertToInvoice(
    id: string | number,
    payload: { invoice_kind?: 'full' | 'deposit' | 'balance'; deposit_rate?: number; deposit_amount?: number } = {}
  ): Observable<any> {
    return this.api.post<{ data: any }>(`kreosi/invoices/convert-to-invoice/${id}`, payload)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error converting quote:', err);
          throw err;
        })
      );
  }

  /**
   * Accept a quote (public endpoint, no auth required)
   */
  accept(uuid: string | number): Observable<any> {
    return this.api.fetch<{ data: any }>(`kreosi/quotes/accept/${uuid}`)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error accepting quote:', err);
          return of(null);
        })
      );
  }

  /**
   * Decline a quote (public endpoint, no auth required)
   */
  reject(uuid: string | number): Observable<any> {
    return this.api.fetch<{ data: any }>(`kreosi/quotes/reject/${uuid}`)
      .pipe(
        map(res => (res as any).data || res),
        catchError(err => {
          console.error('Error declining quote:', err);
          return of(null);
        })
      );
  }

  getByUuid(uuid: string | number): Observable<Quote> {
    return this.api.fetch<Quote>(`kreosi/quotes/preview/${uuid}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error fetching quote:', err);
        return of(null as any);
      })
    );
  }

  /**
   * Get invoice by UUID (public endpoint)
   */
  generatePdf(id: number): Observable<string> {
    return this.api.fetch<any>(`kreosi/invoices/generate-pdf/${id}`, {
      prefix: '',
      paginate: false
    })
    .pipe(
      map(res => (res as any).data || res),
      catchError(err => {
        console.error('Error generating PDF:', err);
        return of(null as any);
      })
    );
  }
}
